import React, { useEffect, useState } from "react";
import { Row, Col, Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from "recharts";

const TopProductsReport = () => {

  const dispatch = useDispatch();
  const user = useSelector(s => s.customer.customer);

  const [rows, setRows] = useState([]);

  /* ================= FILTER STATE ================= */
  const [today, setToday] = useState(false);
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);

  /* ================= LOAD TOP PRODUCTS ================= */
  const loadTopProducts = async ({ today, dateFrom, dateTo }) => {
    dispatch(loadingTrue());

    const params = new URLSearchParams();
    params.append("org_id", user.org_id);

    if (today) params.append("today", 1);
    if (dateFrom) params.append("date_from", dateFrom.toISOString().slice(0, 10));
    if (dateTo) params.append("date_to", dateTo.toISOString().slice(0, 10));

    const res = await Handler({
      method: "get",
      url: `/reports/top_products.php?${params.toString()}`
    });

    dispatch(loadingFalse());

    if (!res?.success) {
      toast.error(res?.msg || "Failed");
      return;
    }

    /* 🔥 safety: data / message */
    const payload = res.data || res.message;
    setRows(payload?.rows || []);
  };

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    if (user?.org_id) {
      loadTopProducts({ today, dateFrom, dateTo });
    }
  }, [user?.org_id]);

  /* ================= AUTO FILTER ================= */
  useEffect(() => {
    if (user?.org_id) {
      loadTopProducts({ today, dateFrom, dateTo });
    }
  }, [today, dateFrom, dateTo]);

  /* ================= HANDLERS ================= */
  const handleToday = () => {
    setToday(!today);
    setDateFrom(null);
    setDateTo(null);
  };

  const handleDateFrom = (d) => {
    setToday(false);
    setDateFrom(d);
  };

  const handleDateTo = (d) => {
    setToday(false);
    setDateTo(d);
  };

  /* ================= TOTALS ================= */
  const totalQty = rows.reduce((s, r) => s + Number(r.qty_sold), 0);
  const totalAmount = rows.reduce((s, r) => s + Number(r.total_amount), 0);

  /* ================= CHART DATA ================= */
  const chartData = rows.map(r => ({
    name: r.product_name,
    Qty: Number(r.qty_sold),
    Amount: Number(r.total_amount)
  }));

  return (
    <>

      {/* ================= FILTER BAR ================= */}
      <div className="filter-bar mb-4">
        <Row className="g-3 align-items-end">
          <Col md={2}>
            <Button
              color={today ? "primary" : "outline-primary"}
              onClick={handleToday}
            >
              Today
            </Button>
          </Col>

          <Col md={3}>
            <label className="small">Date From</label>
            <DatePicker
              selected={dateFrom}
              onChange={handleDateFrom}
              dateFormat="dd-MM-yyyy"
              className="form-control"
              placeholderText="dd-mm-yyyy"
            />
          </Col>

          <Col md={3}>
            <label className="small">Date To</label>
            <DatePicker
              selected={dateTo}
              onChange={handleDateTo}
              dateFormat="dd-MM-yyyy"
              className="form-control"
              placeholderText="dd-mm-yyyy"
              minDate={dateFrom}
            />
          </Col>
        </Row>
      </div>

      {/* ================= KPI CARDS ================= */}
      <Row className="mb-4 g-3">
        <Col md={4}>
          <div className="kpii-card kpii-blue">
            <div className="kpii-label">Top Products</div>
            <div className="kpii-value">{rows.length}</div>
          </div>
        </Col>

        <Col md={4}>
          <div className="kpii-card kpii-purple">
            <div className="kpii-label">Total Qty Sold</div>
            <div className="kpii-value">{totalQty}</div>
          </div>
        </Col>

        <Col md={4}>
          <div className="kpii-card kpii-green">
            <div className="kpii-label">Net Sales</div>
            <div className="kpii-value">₹ {Number(totalAmount).toFixed(2)}</div>
          </div>
        </Col>
      </Row>

      {/* ================= BAR CHART ================= */}
      <Row className="mb-4 g-3">
        <Col md={12}>
          <div className="chart-card" style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} barGap={12}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="Qty" fill="#6366f1" radius={[6,6,0,0]} />
                <Bar dataKey="Amount" fill="#22c55e" radius={[6,6,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Col>
      </Row>

      {/* ================= TABLE ================= */}
      <div className="billing-table-card">
        <div className="table-title">Top Products Summary</div>

        <div className="table-responsive">
          <table className="billing-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th className="right">Qty Sold</th>
                <th className="right">Sales Amount</th>
              </tr>
            </thead>
            <tbody>
              {rows && rows?.map((r, idx) => (
                <tr key={idx}>
                  <td>{idx + 1}</td>
                  <td>{r.product_name}</td>
                  <td className="right">{r.qty_sold}</td>
                  <td className={`right ${Number(r.total_amount) > 0 ? "success" : "muted"}`}>
                    ₹ {Number(r.total_amount).toFixed(2)}
                  </td>
                </tr>
              ))}

              {!rows.length && (
                <tr>
                  <td colSpan="4" className="text-center muted">
                    No data found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

    </>
  );
};

export default TopProductsReport;
